import { INJECTION_GUIDE_ARTICLE_KEY } from "./access";

// TES-1359 / TES-1360 — CARE-GUIDE-01 "Så tar du din injektion", per
// patient. Canon: CP-12 (Drive 12ytP5axeChVM3_DN1s3MNokbbc0ctLQ3nfOZNV8T11c),
// "KANONISKT TILLÄGG 2026-10-14 — INJEKTIONSGUIDE, KONTAKTVÄGAR,
// PENNSTYRNING OCH KÄLLKONTROLL".
//
// The guide stays ONE article key, opened from CP-12 and from Din vård. The
// server personalises what that key returns to a signed-in patient, so every
// installed build gets it without a release:
//   1. the pen module for her pen, when one is published (else the generic
//      guide), and
//   2. the contact paragraph for her actual contact mode (coach or not).

// ---- Pen variants (TES-1360) ----------------------------------------------

// "Första beslutade variantnycklar att stödja är minst: mounjaro_kwikpen,
// wegovy_flextouch, ozempic_pen, kompletterad med exakt produktvariant när
// det krävs för en säker instruktion". Internal keys; never patient-facing.
export const INJECTION_PEN_VARIANTS = [
  "mounjaro_kwikpen",
  "wegovy_flextouch",
  "ozempic_pen",
] as const;
export type InjectionPenVariant = (typeof INJECTION_PEN_VARIANTS)[number];

// The variant is derived ONLY from the medication catalogue entry the doctor
// already picked the order from (TES-1292, `MedicationProduct.key`), so she
// documents nothing twice. Never from free text, an uploaded prescription
// image or AI. An explicit table, not a pattern: a catalogue key that is not
// listed here (a new product, a second Ozempic pen) has no variant until
// someone adds it, and the patient gets the generic guide meanwhile.
//
// The catalogue holds one Ozempic entry (`ozempic`, "förfylld
// injektionspenna") and no exact pen model, so it maps to the generic
// `ozempic_pen`. An exact variant is a new catalogue entry plus a row here.
const CATALOGUE_PRODUCT_TO_PEN_VARIANT: Readonly<
  Record<string, InjectionPenVariant>
> = {
  mounjaro_kwikpen: "mounjaro_kwikpen",
  wegovy_flextouch: "wegovy_flextouch",
  ozempic: "ozempic_pen",
};

/**
 * The pen variant for an order, or null when it is not known for certain.
 *
 * Known only when the order carries a catalogue product AND its medication
 * text is that product's name: an order linked to an entry but written as a
 * different medication ("Annat läkemedel (avvikande)", or free text next to
 * an entry that is not yet in force) is not a safe basis for pen-specific
 * steps.
 */
export function injectionPenVariantForOrder(order: {
  medication: string | null;
  product: { key: string; name: string } | null;
}): InjectionPenVariant | null {
  const p = order.product;
  if (!p) return null;
  const variant = CATALOGUE_PRODUCT_TO_PEN_VARIANT[p.key];
  if (!variant) return null;
  const norm = (s: string | null) => (s ?? "").trim().toLocaleLowerCase("sv");
  if (norm(order.medication) !== norm(p.name)) return null;
  return variant;
}

// The separately versioned module for one pen: its own article key, with
// the article's own version history. A module replaces the whole generic
// guide for that pen ("full patientcopy"); it is never served under its own
// key and never listed, so Mounjaro, Wegovy and Ozempic text cannot cross.
// Until a module is published for a pen, that pen gets the generic guide.
export const INJECTION_GUIDE_MODULE_PREFIX = `${INJECTION_GUIDE_ARTICLE_KEY}.`;

export function injectionGuideModuleKey(variant: InjectionPenVariant): string {
  return `${INJECTION_GUIDE_MODULE_PREFIX}${variant}`;
}

export function isInjectionGuideModuleKey(key: string): boolean {
  return key.startsWith(INJECTION_GUIDE_MODULE_PREFIX);
}

// ---- Contact paragraph (TES-1359) -----------------------------------------

// A paragraph of its own in the guide body (generic or module) that the
// server replaces with the text for her contact mode. Staff previews show
// the token as it is.
export const INJECTION_GUIDE_CONTACT_SLOT = "{{kontaktvag}}";

// "Kontaktvägar i patientcopy", verbatim. "Kontaktvägen ska följa
// patientens faktiska kontaktläge": with coach = a personal coach assigned
// AND coach access active (hasPersonalCoach, the same reader as askVia).
//
// TES-1516 (Ö-645, TES-1458 §2): "Medicinska frågor kan skickas via
// formuläret eller tas upp med aktiv coach för vidareförmedling", so the
// with-coach text offers both routes. The form sentence is the v16 bank's
// medical line, verbatim as TES-1470 (#1649) reuses it; no new copy.
export const INJECTION_GUIDE_CONTACT_TEXT = {
  withCoach:
    "Om du är osäker eller har frågor om läkemedlet kan du skriva till din coach. Coachen hjälper dig vidare och tar medicinska frågor till läkaren. Använd Hjälp och kontakt för en medicinsk fråga. Ta inte en extra dos på eget initiativ.",
  withoutCoach:
    "Om du är osäker eller har frågor om läkemedlet, kontakta Ella och välj Medicinsk fråga. Då ser vi till att frågan kommer till läkaren. Ta inte en extra dos på eget initiativ.",
} as const;

/**
 * The body with the contact slot filled for her contact mode. Only a line
 * that is exactly the slot is replaced, so the token never reaches her as
 * text inside another sentence either way.
 */
export function fillInjectionGuideContact(
  body: string,
  withCoach: boolean,
): string {
  const text = withCoach
    ? INJECTION_GUIDE_CONTACT_TEXT.withCoach
    : INJECTION_GUIDE_CONTACT_TEXT.withoutCoach;
  return body
    .split("\n")
    .map((line) => (line.trim() === INJECTION_GUIDE_CONTACT_SLOT ? text : line))
    .join("\n");
}
